import { config } from '@iheartjane/config';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { useEffect, useState } from 'react';

import {
  useAuthenticateAppleCode,
  useAuthenticateGoogleCode,
} from '@jane/shared/data-access';

import type { AppleAuthError, AppleAuthResponse } from './useAppleSDK';
import { useAppleSDK } from './useAppleSDK';

interface AppleAuthParams {
  first_name?: string;
  id_token: string;
  last_name?: string;
}

export const useThirdPartyAuth = ({
  onAuthError,
  onAuthSuccess,
  storeId,
}: {
  onAuthError: (error: string) => void;
  onAuthSuccess: ({
    newUser,
    ssoChannel,
  }: {
    newUser: boolean;
    ssoChannel: 'apple' | 'google';
  }) => void;
  storeId: number;
}) => {
  const runtimeConfig = useRuntimeConfig();
  const { mutate: authenticateGoogleCode } = useAuthenticateGoogleCode({
    onSuccess: (newUser: boolean) =>
      onAuthSuccess({ ssoChannel: 'google', newUser }),
    onError: () => onAuthError('Google login failed'),
    storeId: storeId,
    appMode: 'kiosk',
  });
  const { mutate: authenticateAppleCode } = useAuthenticateAppleCode({
    onSuccess: (newUser: boolean) =>
      onAuthSuccess({ ssoChannel: 'apple', newUser }),
    onError: () => onAuthError('Apple login failed'),
    storeId: storeId,
    appMode: 'kiosk',
  });

  const [googleLoaded, setGoogleLoaded] = useState(false);
  const {
    isLoaded: appleLoaded,
    initAuth: loadAppleScript,
    signIn,
  } = useAppleSDK();

  useEffect(() => {
    if (!googleLoaded) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      document.body.appendChild(script);
      setGoogleLoaded(true);
    }

    if (!appleLoaded) {
      loadAppleScript();
    }
  }, [googleLoaded, appleLoaded]);

  const handleAppleLogin = (authMetadata: AppleAuthResponse) => {
    const auth: AppleAuthParams = {
      id_token: authMetadata.authorization.id_token,
    };

    if (authMetadata.user) {
      auth.first_name = authMetadata.user?.name?.firstName;
      auth.last_name = authMetadata.user?.name?.lastName;
    }

    try {
      authenticateAppleCode(auth);
    } catch {
      onAuthError('Apple login failed');
    }
  };

  const onAppleLogin = async () => {
    const data = await signIn();

    if ((data as AppleAuthResponse).authorization) {
      handleAppleLogin(data as AppleAuthResponse);
    } else if ((data as AppleAuthError).shouldLogError) {
      onAuthError('Apple login failed');
    }
  };

  const onGoogleLogin = () => {
    const ssoCredentials = runtimeConfig.ssoCredentials;

    const googleClientId = ssoCredentials['google_oauth2'];
    const clientId = googleClientId || config.googleClientId;

    window.google.accounts.oauth2
      .initCodeClient({
        client_id: clientId,
        scope: 'profile email',
        callback: (response) => {
          try {
            authenticateGoogleCode(response);
          } catch {
            onAuthError('Google login failed');
          }
        },
      })
      .requestCode();
  };

  return { onAppleLogin, onGoogleLogin };
};
